/**
 * Single pending invitation row component.
 * Displays invitation details with Resend and Cancel actions.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import RoleBadge from './RoleBadge';
import type { TeamInvitation } from '../../types/company';
import { latnLocale } from '../../utils/numberFormat';

interface PendingInvitationRowProps {
  invitation: TeamInvitation;
  onResend: (id: string) => Promise<void>;
  onCancel: (id: string) => Promise<void>;
}

/**
 * Format a date string to a user-friendly format.
 * e.g., "Jan 15, 2026"
 */
function formatDate(dateString: string, locale: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(latnLocale(locale === 'ar' ? 'ar-SA' : 'en-US'), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Get the expiration status and label for display.
 */
function getExpirationInfo(
  expiresAt: string,
  t: (key: string, options?: string | Record<string, unknown>) => string,
  locale: string
): { label: string; status: 'expired' | 'expiring-soon' | 'valid' } {
  const now = new Date();
  const expDate = new Date(expiresAt);
  const diffMs = expDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffMs <= 0) {
    return { label: t('expired', 'Expired'), status: 'expired' };
  }

  if (diffDays <= 1) {
    return {
      label: t('expiresInHours', 'Expires in < 24h'),
      status: 'expiring-soon',
    };
  }

  if (diffDays <= 3) {
    const translated = t('expiresInDays', { count: diffDays, defaultValue: `Expires in ${diffDays} days` });
    return {
      label: translated,
      status: 'expiring-soon',
    };
  }

  const dateStr = formatDate(expiresAt, locale);
  const translated = t('expiresOn', { date: dateStr, defaultValue: `Expires ${dateStr}` });
  return {
    label: translated,
    status: 'valid',
  };
}

export default function PendingInvitationRow({
  invitation,
  onResend,
  onCancel,
}: PendingInvitationRowProps) {
  const { t, i18n } = useTranslation('team');
  const [isResending, setIsResending] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const isActionInProgress = isResending || isCancelling;
  const { label: expirationLabel, status: expirationStatus } = getExpirationInfo(
    invitation.expiresAt,
    t as (key: string, options?: string | Record<string, unknown>) => string,
    i18n.language,
  );
  const isExpired = expirationStatus === 'expired';

  const handleResend = async () => {
    if (isActionInProgress || isExpired) return;
    setIsResending(true);
    try {
      await onResend(invitation.id);
    } finally {
      setIsResending(false);
    }
  };

  const handleCancel = async () => {
    if (isActionInProgress) return;
    setIsCancelling(true);
    try {
      await onCancel(invitation.id);
    } finally {
      setIsCancelling(false);
    }
  };

  // Expiration status color classes
  const expirationClasses =
    expirationStatus === 'expired'
      ? 'text-red-600 bg-red-50 px-2 py-0.5 rounded-full text-xs font-medium'
      : expirationStatus === 'expiring-soon'
      ? 'text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full text-xs font-medium'
      : 'text-gray-500 text-sm';

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      {/* Email */}
      <td className="px-4 py-4">
        <div className="font-medium text-gray-900">{invitation.email}</div>
      </td>

      {/* Role */}
      <td className="px-4 py-4">
        <RoleBadge role={invitation.role} />
      </td>

      {/* Sent Date */}
      <td className="px-4 py-4 text-gray-500 text-sm">
        {t('sent', 'Sent')} {formatDate(invitation.createdAt, i18n.language)}
      </td>

      {/* Expiration */}
      <td className="px-4 py-4">
        <span className={expirationClasses}>{expirationLabel}</span>
      </td>

      {/* Invited By */}
      <td className="px-4 py-4 text-gray-500 text-sm">
        {invitation.invitedBy || '-'}
      </td>

      {/* Actions */}
      <td className="px-4 py-4">
        <div className="flex items-center gap-2">
          {/* Resend Button */}
          <button
            onClick={handleResend}
            disabled={isActionInProgress || isExpired}
            className={`
              inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg
              border transition-colors
              ${
                isExpired
                  ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                  : isActionInProgress
                  ? 'border-gray-200 text-gray-400 cursor-wait'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
              }
            `}
            title={isExpired ? t('cannotResendExpired', 'Cannot resend expired invitation') : ''}
          >
            {isResending ? (
              <i className="ri-loader-4-line animate-spin" />
            ) : (
              <i className="ri-refresh-line" />
            )}
            {t('resend', 'Resend')}
          </button>

          {/* Cancel Button */}
          <button
            onClick={handleCancel}
            disabled={isActionInProgress}
            className={`
              inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg
              border transition-colors
              ${
                isActionInProgress
                  ? 'border-red-200 text-red-300 cursor-wait'
                  : 'border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400'
              }
            `}
          >
            {isCancelling ? (
              <i className="ri-loader-4-line animate-spin" />
            ) : (
              <i className="ri-close-line" />
            )}
            {t('cancel', 'Cancel')}
          </button>
        </div>
      </td>
    </tr>
  );
}
